import { useEffect, useState } from "react";
import { Button, Col, Dropdown, Input, Row, Skeleton, Space, Table, Tabs } from "antd";

import service from "../../../services/stemiService";
import { recordError } from "../../../utils/logService";
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx-js-style";
import { isAuthorizedRole } from "@seims/shared/src/security";
import moment from "moment";
import DownloadOverviewResults from "./DownloadOverviewResults";
import { map, get } from "lodash";

import {
  FileGeneratorProgress,
  Messages,
  Notify,
  openNotification,
  util,
} from '@seims/shared';

const History = () => {

  const sortDirections = ['ascend', 'descend', 'ascend'];
  const myUploads = "My Uploads";
  const allUploads = "All Uploads";

  const [uploadedFile, setUploadedFile] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [physicalFile, setPhysicalFile] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("File Upload was successful");
  const [showPDF, setShowPDF] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [currentTab, setCurrentTab] = useState(myUploads);
  const [searchText, setSearchText] = useState("");

  const [progress, setProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState("Generating file, please wait...");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    getUploadHistory();
  }, []);

  let msg = "";

  const onReset = () => {
    setProgress(0);
    setProgressText("Generating file, please wait...");
  };

  const tabList = [
    {
      key: myUploads,
      label: "My Uploads"
    },
    {
      key: allUploads,
      label: "All Uploads",
    }
  ]

  const items = [
    {
      label: "PDF - Overview",
      key: "pdf-overview",
      icon: <FilePdfOutlined />,
    },
    {
      label: "EXCEL - Detailed",
      key: "exel-details",
      icon: <FileExcelOutlined />,
      disabled: successful,
      own: true,
    },
  ];

  const columns = [
    {
      title: "File Name",
      dataIndex: "filename",
      key: "filename1",
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "filesize3",
      own: true
    },
    {
      title: "Upload Status",
      dataIndex: "overallStatus",
      key: "overallStatus4",
      sorter: (a, b) => a.overallStatus.localeCompare(b.overallStatus),
      sortDirections: sortDirections,
    },
    {
      title: "Uploaded Date",
      dataIndex: "uploadDate",
      key: "4uploadDate",
      render: (text) => {
        return text != null ? moment(text).format("DD MMM YYYY, HH:mm") : "";
      },
      sorter: (a, b) => a.uploadDate.localeCompare(b.uploadDate),
      sortDirections: sortDirections,
    },
    {
      title: "Download File",
      dataIndex: "file",
      key: "6downloadFile",
      own: true,
      render: (_, record) => {
        const { fileUuid, filename } = record;
        if (record.id > 0) {
          return (
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleFileDownload(fileUuid, filename)}
                />
              </Space>
          );
        }
      },
    },
    {
      title: "Download report",
      dataIndex: "download",
      key: "7downloadReport",
      render: (_, record) => {
        const { reportUuid } = record;
        if (record.id > 0) {
          return (
              <Space>
                <Dropdown 
                menu={{
                  items: items.map((item) => ({
                      ...item,
                      disabled:
                          item.key === 'exel-details' &&
                          record.overallStatus === 'Data Upload Successful',
                  })),
                  onClick: ({ key }) => {
                      handleMenuClick(key, reportUuid);
                  },
              }} >
                  <Space>
                    <Button type="primary">
                      <FilePdfOutlined />
                    </Button>
                  </Space>
                </Dropdown>
              </Space>
          );
        }
      },
    },
    {
      title: "Uploader",
      dataIndex: "user",
      key: "uploader",
      uploader: true,
      sorter: (a, b) => a.user.localeCompare(b.user),
      sortDirections: sortDirections,
    },
  ].filter((item) => {
    if (currentTab === myUploads) {
      return item.uploader === undefined
    } else {
      return isAuthorizedRole(["ADMIN"]) ? item : item.own === undefined
    }
  });

  const handleMenuClick = async (key,reportUuid) => {

    if (key === "pdf-overview") {
      onPDFDownload(reportUuid);
    } else if (key === "exel-details") {
      onDownloadExcel(reportUuid);

    }
  }

  const generateEXCEL = async (dataFile) => {

    if(!dataFile || dataFile.length == 0) {
      return;
    }
    const { details: reportData, fileDetails } = dataFile;

    const response = await service.downloadOriginalFile(fileDetails.uuid);
    if (response) {
      const { data } = response;
      const blobFile = new Blob([data], { type: 'application/octet-stream' });
      // setPhysicalFile(blobFile);

      await new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        setIsGenerating(true);

        fileReader.readAsArrayBuffer(blobFile);
        fileReader.onload = async (e) => {
          const bufferArray = e.target.result;
          const wb = XLSX.read(bufferArray, { type: "buffer" });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          // const data = XLSX.utils.sheet_to_json(ws);

          let colAlias = "";

          for (let rowIndex = 0; rowIndex < reportData.length; rowIndex++) {
            //START FROM COLUMN A (StemiName) - COLUMN R (LEARNER RESULTS)
            if (reportData[rowIndex].col === 0) colAlias = "A";
            if (reportData[rowIndex].col === 1) colAlias = "B";
            if (reportData[rowIndex].col === 2) colAlias = "C";
            if (reportData[rowIndex].col === 3) colAlias = "D";
            if (reportData[rowIndex].col === 4) colAlias = "E";
            if (reportData[rowIndex].col === 5) colAlias = "F";
            if (reportData[rowIndex].col === 6) colAlias = "G";
            if (reportData[rowIndex].col === 7) colAlias = "H";
            if (reportData[rowIndex].col === 8) colAlias = "I";
            if (reportData[rowIndex].col === 9) colAlias = "J";
            if (reportData[rowIndex].col === 10) colAlias = "K";
            if (reportData[rowIndex].col === 11) colAlias = "L";
            if (reportData[rowIndex].col === 12) colAlias = "M";
            if (reportData[rowIndex].col === 13) colAlias = "N";
            if (reportData[rowIndex].col === 14) colAlias = "O";
            if (reportData[rowIndex].col === 15) colAlias = "P";
            if (reportData[rowIndex].col === 16) colAlias = "Q";
            if (reportData[rowIndex].col === 17) colAlias = "R";

            const currRow = reportData[rowIndex].row + 1;
            const cell = ws[colAlias + currRow];

            if (cell) {
              XLSX.utils.cell_add_comment(
                cell,
                reportData[rowIndex].message,
                "CSIR"
              );
            }
          }

          const range = XLSX.utils.decode_range(ws["!ref"]);
          

          for (let i = range.s.r; i <= range.e.r; i++) {
            if ((i / range.e.r) * 100 < 99) {
              await new Promise((resolve) => setTimeout(resolve, 0));
              setProgress(Math.round((i / range.e.r) * 100));
            }

            for (let j = range.s.c; j <= 18; j++) {
              const cell = ws[XLSX.utils.encode_cell({ r: i, c: j })];

              const errorFound = reportData.filter(
                (current) => current.row === i && current.col === j
              );

              if (cell && errorFound.length > 0) {
                cell.s = { fill: { fgColor: { rgb: "ffff00" } } };
              }
            }
          }
          setProgressText("Downloading file...");

          const workbook = XLSX.utils.book_new();

          XLSX.utils.book_append_sheet(workbook, ws, wsname);

          const fileInfo = fileDetails.filename.split(".");
          const lastIndex = fileInfo.length - 1;
          XLSX.writeFile(
            wb,
            `${fileInfo[0]}_WITH_COMMENTS.${fileInfo[lastIndex]}`
          );

          setProgress(100);

          if (dataFile.length > 0) {
            resolve(dataFile);
            setProgressText("Done");

            if (!isGenerating) {
              onReset();
            }
          }
          setIsGenerating(false);
        };
        FileReader.onerror = (error) => {
          reject(error);
          setLoading(true);
          setIsGenerating(false);
        };
      });
    }
  }

  const onPDFDownload = async (reportUuid) => {
    try {
      // const response = await service.downloadReport(reportUuid);
      const response = await service.downloadOverviewReport(reportUuid);
      if (response && response.status === 200) {
        const { data } = response;

        if (data.overallStatus === "Data Upload Failed") {
          setShowPDF(true);
          setReportData(data);
          setSuccessful(false);
        } else {
          setShowPDF(true);
          setReportData(data);
          setSuccessful(true);
        }
      }
    } catch (error) { }
  };

  const onDownloadExcel = async (reportUuid) => {

    const response = await service.downloadReport(reportUuid, currentTab);
    if (response && response?.status === 200) {
      const { data } = response;
      if (data.overallStatus === "Data Upload Failed") {
        setReportData(data);
        setSuccessful(false);
      } else {
        setReportData(data);
        setSuccessful(true);
      }
      generateEXCEL(data);
    };
  }

  const onTabChange = (selectedTab) => {
    setCurrentTab(selectedTab);
    getUploadHistory(selectedTab);
  };

  const getUploadHistory = async (selectedTab = currentTab) => {
    const entity = "file";
    setUploadedFile([]);
    try {
      setLoading(true);
      const response = (selectedTab === myUploads) ? await service.getUploadHistory(true) : await service.getUploadHistory(false);
      if (response && response.status === 200) {
        var fileData = [];
        const { data } = response;

        fileData = data.map((element) => ({
          fileUuid: element.uploadedFile.uuid.toString(),
          reportUuid: element.uploadReport.uuid.toString(),
          id: element.uploadedFile.id,
          overallStatus: element.overallStatus,
          filename: element.uploadedFile.filename,
          fileSize: `${Math.round(element.uploadedFile.fileSize / 1024, 0)}KB`,
          uploadDate: element.uploadDate,
          user: element.ownRecord? "Me":
            element.userDetails?.firstname + " " + element.userDetails?.surname,
          own: element.ownRecord,
        }));
        setUploadedFile(fileData);
        setFilteredData(fileData);
        setTotal(fileData.length);

        setLoading(false);
      } else if (response && response.status === 204) {
        setLoading(false);
        setUploadedFile([]);
        setFilteredData([]);
      } else {
        setUploadedFile([]);
        setFilteredData([]);
      }
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, entity);
      openNotification(
        "System failed to load uploaded file details",
        msg,
        Notify.ERROR
      );
      recordError(error);
    }
  };

  const handleFileDownload = async (fileUuid, filename) => {
    try {
      const response = await service.downloadOriginalFile(fileUuid);
      if (response) {
        const { data } = response;
        setPhysicalFile(data);
        const new_blob = new Blob([data]);

        const url = URL.createObjectURL(new_blob);
        const downloadLink = document.createElement("a");

        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      msg = util.parseMessage(Messages.FILEDOWNLOAD_FAILURE, "File");
      openNotification("File Download", msg, Notify.ERROR);

      recordError(error);
    }
  };

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };
  const handleCancel = () => {
    setShowPDF(false);
  };
  const handleInputChange = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    globalSearch(searchValue);
};

const globalSearch = (searchValue) => {
  if (searchText === "") {
      setFilteredData(uploadedFile);
      return;
  }

  const reg = new RegExp(
      searchValue.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"),
      "gi"
  );

  var _filteredData = map(uploadedFile, (record) => {
      const questionMatch = get(record, "overallStatus").match(reg);
      const answerMatch = get(record, "user").match(reg);

      if (!questionMatch && !answerMatch) {
          return null;
      }
      return record;
  }).filter((record) => record);

  setSearchText(searchValue);
  setFilteredData(_filteredData);
};
  const title = "STEMI Olympiad - Data Upload History";
  const subTitle = "List of previously uploaded files";

  return (
    <div className="content">
      <h1 className="center-align">{title}</h1>
      <div>{subTitle}</div>
      <Row>
        <Col xs={24} md={8}>
          <Input
            placeholder="Start typing to filter documents by Upload Status or User"
            type="text"
            className="search-bar"
            onChange={handleInputChange}
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            name=''
          />
        </Col>
      </Row>
      <div className="space"></div>
      {isAuthorizedRole(["STEMI_LEAD_AGENT", "ADMIN"]) && (
        <Tabs
          type="card"
          className="event-information"
          items={tabList}
          key="1"
          onChange={onTabChange}
        />
      )}
      {loading ? (
        <Skeleton active avatar />
      ) : (
        <Table
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          rowKey={(record) => "Upload-" + record.id}
          bordered
          size="middle"
          onChange={onRowChange}
          pagination={{
            total: filteredData.length,
            onChange: (page, pageSize) => {
                setCurrentPage(page); // manage current page in state
                setPageSize(pageSize);
            },
            showSizeChanger: true,
            showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${total > 1 ? 'Uploads' : 'Upload'}`
        }}
        />
      )}
      <DownloadOverviewResults
        show={showPDF}
        successful={successful}
        onCancel={handleCancel}
        data={reportData}
      />
       <FileGeneratorProgress progressText={progressText} progress={progress} setProgress isGenerating={isGenerating} />
    </div>
  );
};

export default History;
